import { useState } from 'react';
import Button from '../common/Button';
import Input from '../common/Input';
import { settingsApi } from '../../api/settings';
import { useUIStore } from '../../stores/uiStore';

/**
 * Test Email Provider Modal
 * Modal for testing email provider connections (SMTP or Microsoft Graph)
 * Configuration is fetched from database server-side
 */
export default function TestEmailProviderModal({ provider, onClose }) {
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const { showToast } = useUIStore();

  const handleTest = async () => {
    if (!testEmail) {
      showToast('Please enter a test email address', 'error');
      return;
    }

    try {
      setTesting(true);
      setResult(null);

      // Server fetches config from database - no need to pass it
      const response = await settingsApi.testEmailProvider({
        provider: provider,
        test_recipient: testEmail
      });

      setResult({
        success: true,
        message: response.data.message
      });
      showToast('Test email sent successfully!', 'success');
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Test failed';
      setResult({
        success: false,
        message: errorMessage
      });
      showToast(errorMessage, 'error');
    } finally {
      setTesting(false);
    }
  };

  const providerName = provider === 'smtp' ? 'SMTP' : 'Microsoft Graph API';

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Test {providerName} Connection
          </h3>
        </div>

        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-gray-600">
            Enter an email address to receive a test email via {providerName}.
          </p>

          <Input
            label="Test Email Address"
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="test@example.com"
            required
          />

          {result && (
            <div className={`rounded-lg p-4 ${
              result.success 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex">
                <div className="flex-shrink-0">
                  {result.success ? (
                    <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <div className="ml-3">
                  <p className={`text-sm ${
                    result.success ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {result.message}
                  </p>
                </div>
              </div>
            </div>
          )}

          {provider === 'graph' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-700">
                <strong>Note:</strong> Ensure your Azure AD app has <strong>Mail.Send</strong> permission 
                and admin consent has been granted.
              </p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-gray-50 flex justify-end gap-2 rounded-b-lg">
          <Button
            variant="secondary"
            onClick={onClose}
          >
            Close
          </Button>
          <Button
            onClick={handleTest}
            loading={testing}
            disabled={!testEmail}
          >
            Send Test Email
          </Button>
        </div>
      </div>
    </div>
  );
}